/**
 * backdata (백데이터) orval 래퍼
 */
import { customInstance } from "../custom-instance";
import { DefaultBasicInfoSchema } from "./default-schemes";
import { validateArrayWithSchema } from "@erp/shared/utils/https/zod-validation";
import z from "zod";
// ── 스키마 & 타입 (Spot) ──
export const GetSpotsResponseSchema = z.object({
    ...DefaultBasicInfoSchema.shape,
    id: z.coerce.number(),
    spot: z.string().nullable(),
});
// ── 스키마 & 타입 (Manufacturer) ──
export const GetManufacturersResponseSchema = z.object({
    ...DefaultBasicInfoSchema.shape,
    id: z.coerce.number(),
    manufacturer: z.string(),
});
// ── 스키마 & 타입 (Classification) ──
export const GetClassificationsResponseSchema = z.object({
    ...DefaultBasicInfoSchema.shape,
    id: z.coerce.number(),
    classification: z.string(),
});
// ── 스키마 & 타입 (BasicInfo 통합) ──
const GetBasicInfoResponseSchema = z.object({
    classifications: GetClassificationsResponseSchema.array(),
    manufacturers: GetManufacturersResponseSchema.array(),
    spots: GetSpotsResponseSchema.array(),
});
// ── 스키마 & 타입 (Department) ──
export const GetDepartmentsResponseSchema = z.object({
    ...DefaultBasicInfoSchema.shape,
    id: z.number(),
    departmentName: z.string(),
});
// ── 스키마 & 타입 (Position) ──
export const GetPositionsResponseSchema = z
    .object({
    ...DefaultBasicInfoSchema.shape,
    id: z.coerce.number(),
    positionName: z.string(),
})
    .strict();
// ── 스키마 & 타입 (LaborCostCalc) ──
export const GetLaborCostCalcResponseSchema = z.object({
    ...DefaultBasicInfoSchema.shape,
    id: z.coerce.number(),
    no: z.string(),
    name: z.string(),
    type: z.string(),
    occupation: z.string(),
    manPerDay: z.number(),
    surchargeRatio: z.number(),
});
// ── 스키마 & 타입 (OutputRatio) ──
export const GetOutputRatioResponseSchema = z.object({
    ...DefaultBasicInfoSchema.shape,
    id: z.coerce.number(),
    type: z.string(),
    formula: z.string(),
    ratio: z.number(),
});
// ── 스키마 & 타입 (MarketWageUnitPrice) ──
export const GetMarketWageUnitPriceResponseSchema = z.object({
    ...DefaultBasicInfoSchema.shape,
    id: z.coerce.number(),
    occupation: z.string(),
    unitPrice: z.number(),
    adjustmentRatio: z.number(),
});
// ── 스키마 & 타입 (SystemConfig) ──
export const GetSystemConfigResponseSchema = z.object({
    id: z.number(),
    configKey: z.string(),
    configValue: z.string(),
    description: z.string().nullable(),
    category: z.string().nullable(),
});
// ── API 함수 ──
export async function getBasicInfo() {
    return customInstance({
        url: "/search/options",
        method: "GET",
    });
}
export async function getSpots() {
    const data = await customInstance({
        url: "/spot",
        method: "GET",
    });
    return validateArrayWithSchema(GetSpotsResponseSchema, data);
}
export async function postSpot(body) {
    return customInstance({ url: "/spot", method: "POST", headers: { "Content-Type": "application/json" }, data: body });
}
export async function patchSpot(id, body) {
    return customInstance({ url: `/spot/${id}`, method: "PATCH", headers: { "Content-Type": "application/json" }, data: body });
}
export async function deleteSpot(id) {
    return customInstance({ url: `/spot/${id}`, method: "DELETE" });
}
export async function getManufacturers() {
    const data = await customInstance({
        url: "/manufacturer",
        method: "GET",
    });
    return validateArrayWithSchema(GetManufacturersResponseSchema, data);
}
export async function postManufacturer(body) {
    return customInstance({ url: "/manufacturer", method: "POST", headers: { "Content-Type": "application/json" }, data: body });
}
export async function patchManufacturer(id, body) {
    return customInstance({ url: `/manufacturer/${id}`, method: "PATCH", headers: { "Content-Type": "application/json" }, data: body });
}
export async function deleteManufacturer(id) {
    return customInstance({ url: `/manufacturer/${id}`, method: "DELETE" });
}
export async function getClassifications() {
    const data = await customInstance({
        url: "/classification",
        method: "GET",
    });
    return validateArrayWithSchema(GetClassificationsResponseSchema, data);
}
export async function postClassification(body) {
    return customInstance({ url: "/classification", method: "POST", headers: { "Content-Type": "application/json" }, data: body });
}
export async function patchClassification(id, body) {
    return customInstance({ url: `/classification/${id}`, method: "PATCH", headers: { "Content-Type": "application/json" }, data: body });
}
export async function deleteClassification(id) {
    return customInstance({ url: `/classification/${id}`, method: "DELETE" });
}
export async function getDepartments() {
    const data = await customInstance({
        url: "/department",
        method: "GET",
    });
    return validateArrayWithSchema(GetDepartmentsResponseSchema, data);
}
export async function getPositions() {
    const data = await customInstance({
        url: "/position",
        method: "GET",
    });
    return validateArrayWithSchema(GetPositionsResponseSchema, data);
}
export async function getLaborCostCalc() {
    const data = await customInstance({
        url: "/labor-cost",
        method: "GET",
    });
    return validateArrayWithSchema(GetLaborCostCalcResponseSchema, data);
}
export async function postLaborCostCalc(body) {
    return customInstance({ url: "/labor-cost", method: "POST", headers: { "Content-Type": "application/json" }, data: body });
}
export async function patchLaborCostCalc(id, body) {
    return customInstance({ url: `/labor-cost/${id}`, method: "PATCH", headers: { "Content-Type": "application/json" }, data: body });
}
export async function deleteLaborCostCalc(id) {
    return customInstance({ url: `/labor-cost/${id}`, method: "DELETE" });
}
export async function getOutputRatio() {
    const data = await customInstance({
        url: "/cost-ratio",
        method: "GET",
    });
    return validateArrayWithSchema(GetOutputRatioResponseSchema, data);
}
export async function postOutputRatio(body) {
    return customInstance({ url: "/cost-ratio", method: "POST", headers: { "Content-Type": "application/json" }, data: body });
}
export async function patchOutputRatio(id, body) {
    return customInstance({ url: `/cost-ratio/${id}`, method: "PATCH", headers: { "Content-Type": "application/json" }, data: body });
}
export async function deleteOutputRatio(id) {
    return customInstance({ url: `/cost-ratio/${id}`, method: "DELETE" });
}
export async function getMarketWageUnitPrice() {
    const data = await customInstance({
        url: "/labor-current-unit-price",
        method: "GET",
    });
    return validateArrayWithSchema(GetMarketWageUnitPriceResponseSchema, data);
}
export async function postMarketWageUnitPrice(body) {
    return customInstance({ url: "/labor-current-unit-price", method: "POST", headers: { "Content-Type": "application/json" }, data: body });
}
export async function patchMarketWageUnitPrice(id, body) {
    return customInstance({ url: `/labor-current-unit-price/${id}`, method: "PATCH", headers: { "Content-Type": "application/json" }, data: body });
}
export async function deleteMarketWageUnitPrice(id) {
    return customInstance({ url: `/labor-current-unit-price/${id}`, method: "DELETE" });
}
export async function getSystemConfigs(category) {
    const data = await customInstance({
        url: "/system-config",
        method: "GET",
        params: category ? { category } : {},
    });
    return validateArrayWithSchema(GetSystemConfigResponseSchema, data);
}
export async function patchSystemConfig(key, body) {
    return customInstance({ url: `/system-config/${key}`, method: "PATCH", headers: { "Content-Type": "application/json" }, data: body });
}
// ── SDK 객체 ──
export const backdataApi = {
    getBasicInfo,
    getSpots,
    postSpot,
    patchSpot,
    deleteSpot,
    getManufacturers,
    postManufacturer,
    patchManufacturer,
    deleteManufacturer,
    getClassifications,
    postClassification,
    patchClassification,
    deleteClassification,
    getDepartments,
    getPositions,
    getLaborCostCalc,
    postLaborCostCalc,
    patchLaborCostCalc,
    deleteLaborCostCalc,
    getOutputRatio,
    postOutputRatio,
    patchOutputRatio,
    deleteOutputRatio,
    getMarketWageUnitPrice,
    postMarketWageUnitPrice,
    patchMarketWageUnitPrice,
    deleteMarketWageUnitPrice,
    getSystemConfigs,
    patchSystemConfig,
};
